import { useTranslation } from 'next-i18next'
import { createElement, useEffect, useState } from 'react'

import {
  ContentfulContentContainer,
  ContentfulContentShowMoreLink,
  ContentfulContentStyled,
} from '@/components/shared/contentful-content/styled'
import { type ContentfulDataContent } from '@/types/contentful'
import { sendToLogger } from '@/utils/sendToLogger'
import { isBrowserSafari } from '@/utils/userAgent'

type ContentfulContentProps = {
  userAgent?: string
  textJson: Promise<ContentfulDataContent | undefined>
}

export const ContentfulContent = ({
  userAgent = '',
  textJson,
}: ContentfulContentProps): React.ReactNode => {
  const { t } = useTranslation('common')
  const [isShowingMore, setIsShowingMore] = useState(false)
  const [content, setContent] = useState<ContentfulDataContent>([])

  const isSafari = isBrowserSafari(userAgent)

  useEffect(() => {
    const loadContent = async () => {
      try {
        if (textJson instanceof Promise) {
          const resolvedContent = await textJson

          if (resolvedContent) {
            setContent(resolvedContent)
          }
        } else if (Array.isArray(textJson)) {
          setContent(textJson)
        }
      } catch (err) {
        sendToLogger(err, { fingerprint: ['ContentfulContent'] })
      }
    }

    loadContent()
  }, [textJson])

  if (!content || !Array.isArray(content)) return null

  const innerContent = content.map(({ type, content }, index) => {
    if (!type) return null

    return createElement(type, {
      dangerouslySetInnerHTML: { __html: content },
      key: `ContentfulContent-${type}-${index}`,
    })
  })

  return (
    <>
      {innerContent.length > 0 && (
        <ContentfulContentContainer isSafari={isSafari} isShowingMore={isShowingMore}>
          <ContentfulContentStyled
            isShowingMore={isShowingMore}
            isSafari={isSafari}
            key="ContentfulContent"
          >
            {innerContent}
          </ContentfulContentStyled>

          {isShowingMore || !isSafari ? '' : '...'}

          <ContentfulContentShowMoreLink
            $isShowingMore={isShowingMore}
            data-testid="contentful-content-show-link"
            hasUnderline
            onClick={() => setIsShowingMore((prev) => !prev)}
            variant="secondary"
          >
            {isShowingMore ? t('show.less') : t('read.more')}
          </ContentfulContentShowMoreLink>
        </ContentfulContentContainer>
      )}
    </>
  )
}
